import React from 'react';
import './EventCard.css';
import BodySmallRegular from '../designSystem/BodySmallRegular';
import BodySmallMedium from '../designSystem/BodySmallMedium';
import BodyMediumMedium from '../designSystem/BodyMediumMedium';
import clockIcon from "../../assets/images/clockIcon.svg";
import practiceLocationIcon from "../../assets/images/Icon=Practice Locations.svg";
import doctorIcon from "../../assets/images/Icon=Doctors.svg";
import morningIcon from "../../assets/images/morningIcon.svg";
import eveningIcon from "../../assets/images/eveningIcon.svg";
import cityIcon from "../../assets/images/Icon=City.svg";
import productIcon from "../../assets/images/Icon=Products.svg";
import subtaskIcon from "../../assets/images/subtaskIcon.svg";
import teamIcon from "../../assets/images/Icon=Team.svg";

const EventCard = ({ event, planCityName, taskLookups }) => {
    const formatTime = (time) => {
        if (!time) return '';
        const [hour, minute] = time.split(":");
        const hourInt = parseInt(hour, 10);
        const isPM = hourInt >= 12;
        const formattedHour = hourInt % 12 || 12;
        return `${formattedHour}:${minute} ${isPM ? 'PM' : 'AM'}`;
    };

    const startTimeFormatted = formatTime(event?.startTime);
    const endTimeFormatted = formatTime(event?.endTime);
    
    if (!event) return null;

    // Check if this is a simplified plan (only city or head office, no time/location/doctor details)
    const isSimplifiedPlan = event?.headOffice || 
                              (!event?.startTime && !event?.endTime && 
                               !event?.practiceLocationName && !event?.doctorName && 
                               event?.cityName);

    // Render simplified view for head office or city-only plans
    if (isSimplifiedPlan) {
        return (
            <div className="event-card">
                {event?.headOffice ? (
                    <div className="event-card-header">
                        <div className="event-card-title">
                            <BodyMediumMedium text="Head Office" color='#0F172A' />
                        </div>
                    </div>
                ) : event?.cityName ? (
                    <div className="event-card-header">
                        <div className="event-card-title">
                            <BodyMediumMedium text={event.cityName} color='#0F172A' />
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
    
    // Get priority details from taskLookups
    const getPriorityDetails = (priorityId) => {
        if (!priorityId || !taskLookups) return null;
        
        const priorities = taskLookups?.taskPriorities?.result || taskLookups?.taskPriorities;
        if (!Array.isArray(priorities)) return null;
        
        const priority = priorities.find(p => p.id === priorityId);
        return priority || null;
    };

    const priorityDetails = getPriorityDetails(event?.priority);
    const priorityText = priorityDetails?.name || null;
    const priorityColor = priorityDetails?.color || '#DC2626'; // Default to red if no color

    return (
        <div className="event-card">
            <div className="event-card-header">
                <div className="event-card-title">
                    {event?.doctorName && (
                        <BodyMediumMedium text={event.doctorName} color='#0F172A' />
                    )}
                </div>
                {priorityText && (
                    <span 
                        className="priority-badge-dynamic"
                        style={{ 
                            color: priorityColor,
                            backgroundColor: `${priorityColor}15` // 15 is hex for ~8% opacity
                        }}
                    >
                        • {priorityText}
                    </span>
                )}
            </div>

            {event?.practiceLocationName && (
                <div className='practice-location-text'>
                    <BodySmallRegular text={event.practiceLocationName} color='#64748B' />
                </div>
            )}

            {event?.cityName && (
                <div className="eventcard-content-container">
                    <img src={cityIcon} alt="city" />
                    <BodySmallRegular text={event.cityName} color='#384153' />
                </div>
            )}

            {event?.startTime && event?.endTime && (
                <div className="eventcard-content-container">
                    <img src={clockIcon} alt="clock" />
                    <BodySmallRegular
                        text={`${startTimeFormatted} - ${endTimeFormatted}`}
                        color='#384153' />
                    {event?.shiftId === 1 && (
                        <img src={morningIcon} alt="morning" />
                    )}
                    {event?.shiftId === 2 && (
                        <img src={eveningIcon} alt="evening" />
                    )}
                </div>
            )}

            {/* Products Section */}
            {event?.products && event.products.length > 0 && (
                <div className="event-section">
                    <div className="section-header">
                        <img src={productIcon} alt="products" />
                        <BodySmallMedium text="Products" color='#384153' />
                    </div>
                    <div className="products-container">
                        {event.products.map((product, index) => (
                            <span key={product.productId || index} className="product-pill">
                                {product.productName}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Subtasks Section */}
            {event?.subTasks && event.subTasks.length > 0 && (
                <div className="event-section">
                    <div className="section-header">
                        <img src={subtaskIcon} alt="subtasks" />
                        <BodySmallMedium text="Subtasks" color='#384153' />
                    </div>
                    <div className="subtasks-container">
                        {event.subTasks.map((subtask, index) => (
                            <span key={subtask.subTaskId || index} className="subtask-item">
                                {subtask.subTaskName}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Accompanied By Section - Only show if there are accompanied by entries */}
            {event?.accompaniedBy && event.accompaniedBy.length > 0 && (
                <div className="event-section">
                    <div className="section-header">
                        <img src={teamIcon} alt="accompanied by" />
                        <BodySmallMedium text="Accompanied by" color='#384153' />
                    </div>
                    <div className="accompanied-by-container">
                        {event.accompaniedBy.map((person, index) => (
                            <span key={person.accompaniedBy || index} className="accompanied-by-item">
                                {person.accompaniedByName}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventCard;